import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '../../../../store/index';
import {
  Button,
  useTheme,
  Typography,
  Tooltip,
  Fab,
  LinearProgress,
  makeStyles,
  Divider,
  useMediaQuery,
} from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import BarChartIcon from '@material-ui/icons/BarChart';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import { ExitToApp } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  button_group_container: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 4px',
  },

  tooltip_text: {
    letterSpacing: '0.1rem',
    fontSize: theme.fonts.responsiveMedium,
  },

  summary_fabs: {
    margin: '4px',
    height: '22px',
    width: '22px',
    minHeight: '22px',
    minWidth: '22px',
    backgroundColor: theme.palette.accents.dark,

    '&:hover': {
      background: `${theme.palette.accents.main} !important`,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  actionButton: {
    color: 'white',
    minWidth: '80px',
    height: '18px',
    minHeight: '18px',
    margin: '4px',
    fontSize: theme.fonts.responsiveSmall,
    backgroundColor: theme.palette.accents.dark,
    '&:hover': {
      background: `${theme.palette.accents.main} !important`,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  icon_button: {
    height: '16px',
    width: '16px',
    minHeight: '16px',
    minWidth: '16px',
    color: 'white',
  },
}));

const SummaryCardActions = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = useStyles();

  // Store state
  const { applied_quality, confirmed_quality, shown_quality } = useSelector((state) => state.results);
  const { pendingReprocessing } = useSelector((state) => state.examinations);
  const { savingPending } = useSelector((state) => state.labeling);
  const { tabsIndex, examinationListDrawer } = useSelector((state) => state.gui);
  const { premium_features } = useSelector((store) => store.global_settings || {});

  const { data_annotation } = premium_features;

  const annotatorRole = data_annotation && localStorage.getItem('annotation') === 'true';
  const currentRole = localStorage.getItem('role');
  const isConfirmed = applied_quality === 'bbox' && confirmed_quality;
  const isRelabeled = confirmed_quality && applied_quality !== 'bbox';
  const revisedQuality = isConfirmed || isRelabeled;
  const shownQuality = shown_quality || applied_quality;

  const smallScreen = useMediaQuery('(max-width: 1400px)') || examinationListDrawer;

  // Local state

  const handleRevisionMode = () => {
    dispatch(actions.setTabsIndex(2));
    dispatch(actions.setQualityInRevisionMode(true));
    dispatch(actions.setShownQualityOverlayType(''));
    dispatch(actions.setQualityInDrawingMode(false));
  };

  const onConfirmClick = () => {
    const state = shown_quality === applied_quality && confirmed_quality;
    dispatch(actions.confirmEvaluation(shownQuality, 'quality', !state, t));
  };

  const exitRevisionMode = () => {
    dispatch(actions.handleRevisionExit());
  };

  return (
    <>
      {savingPending ? (
        <div className={classes.button_group_container}>
          <LinearProgress
            style={{
              width: '98%',
            }}
          />
        </div>
      ) : (
        <div className={classes.button_group_container}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            {tabsIndex === 0 && currentRole === 'admin' && (
              <>
                {/* Labeling statistics Fab */}
                <Tooltip
                  placement='top-end'
                  arrow
                  title={
                    <Typography className={classes.tooltip_text} color='inherit'>
                      {t('ResultView.labelingStatistics')}
                    </Typography>
                  }
                >
                  <span>
                    <Fab
                      size='small'
                      className={classes.summary_fabs}
                      onClick={() => dispatch(actions.setActiveDialog('LabelStat'))}
                      color='primary'
                      style={{ marginLeft: '12px' }}
                    >
                      <BarChartIcon fontSize='small' className={classes.icon_button} />
                    </Fab>
                  </span>
                </Tooltip>
              </>
            )}

            {tabsIndex === 1 && (
              <Tooltip
                title={
                  <Typography className={classes.tooltipText}>{t('ResultView.btn_exit_revision')}</Typography>
                }
                arrow
                placement='top-start'
              >
                <>
                  {smallScreen ? (
                    <Fab
                      size='small'
                      onClick={() => exitRevisionMode()}
                      color='primary'
                      className={classes.summary_fabs}
                    >
                      <ExitToApp
                        fontSize='small'
                        style={{ width: '18px', height: '18px', transform: 'scaleX(-1)' }}
                      />
                    </Fab>
                  ) : (
                    <Button
                      component='label'
                      variant='contained'
                      size='small'
                      id='exit_revision_mode'
                      className={classes.actionButton}
                      onClick={() => exitRevisionMode()}
                    >
                      <ExitToApp
                        fontSize='small'
                        style={{ width: '18px', height: '18px', transform: 'scaleX(-1)', marginRight: '4px' }}
                      />
                      {t('ResultView.btn_exit')}
                    </Button>
                  )}
                </>
              </Tooltip>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* Report button*/}
            {tabsIndex === 0 &&
              (smallScreen ? (
                <Fab
                  disabled={pendingReprocessing}
                  id='generate_report_button_fab'
                  size='small'
                  className={classes.summary_fabs}
                  onClick={() => dispatch(actions.setActiveDialog('report'))}
                  color='primary'
                  style={{ marginLeft: '12px' }}
                >
                  <DescriptionIcon
                    fontSize='small'
                    className={classes.icon_button}
                    style={{
                      color: pendingReprocessing ? 'gray' : 'white',
                    }}
                  />
                </Fab>
              ) : (
                <Button
                  disabled={pendingReprocessing}
                  component='label'
                  variant='contained'
                  size='small'
                  id='generate_report_button'
                  className={classes.actionButton}
                  onClick={() => dispatch(actions.setActiveDialog('report'))}
                  startIcon={
                    <DescriptionIcon
                      fontSize='small'
                      className={classes.icon_button}
                      style={{
                        color: pendingReprocessing ? 'gray' : 'white',
                      }}
                    />
                  }
                >
                  {t('ResultView.report')}
                </Button>
              ))}

            {/* Confirm or dicard quality */}
            {tabsIndex === 1 && annotatorRole && !revisedQuality && (
              <>
                <Tooltip
                  arrow
                  placement='top'
                  title={
                    <>
                      <Typography className={classes.tooltip_text} style={{ fontWeight: 'bold' }}>
                        {t('ResultView.confirm_current')}
                      </Typography>
                    </>
                  }
                >
                  {smallScreen ? (
                    <Fab
                      color='primary'
                      className={classes.summary_fabs}
                      onClick={() => {
                        onConfirmClick();
                      }}
                    >
                      <CheckIcon fontSize='small' className={classes.icon_button} />
                    </Fab>
                  ) : (
                    <Button
                      color='primary'
                      variant='contained'
                      // startIcon={
                      //   <CheckIcon
                      //     fontSize='medium'
                      //     style={{ width: '14px', height: '14px', marginRight: '2px' }}
                      //   />
                      // }
                      style={{
                        color: '#fff',
                        margin: '4px',
                        textWrap: 'nowrap',
                        // backgroundColor: '#515151',
                      }}
                      className={classes.actionButton}
                      onClick={() => {
                        onConfirmClick();
                      }}
                    >
                      {t('ResultView.confirm')}
                    </Button>
                  )}
                </Tooltip>
              </>
            )}
            {/* Enter revision mode  */}
            {tabsIndex === 1 && annotatorRole && (
              <Tooltip
                arrow
                placement='left-end'
                title={
                  <>
                    <Typography className={classes.tooltip_text} color='inherit'>
                      {tabsIndex === 1
                        ? t('ResultView.tooltip_revise_quality')
                        : t('ResultView.tooltip_revise_diagnostics')}
                    </Typography>
                  </>
                }
              >
                {smallScreen ? (
                  <Fab
                    size='small'
                    onClick={() => handleRevisionMode()}
                    color={!!confirmed_quality ? 'primary' : 'default'}
                    className={classes.summary_fabs}
                  >
                    <EditIcon fontSize='small' className={classes.icon_button} />
                  </Fab>
                ) : (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => handleRevisionMode()}
                    color='primary'
                    // startIcon={<EditIcon fontSize='small' className={classes.icon_button} />}
                    className={classes.actionButton}
                  >
                    {t('ResultView.relable')}
                  </Button>
                )}
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SummaryCardActions;
